import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlusCircle, Files } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

// Import refactored components
import DashboardStats from './dashboard/DashboardStats';
import CoursesList from './dashboard/CoursesList';
import { useDashboardData, formatDate } from './dashboard/useDashboardData';

const AdminDashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  
  // Use auth redirect hook to handle authentication
  useAuthRedirect(false);
  
  // Add explicit debug code to ensure path is saved
  useEffect(() => {
    if (!isAuthenticated) {
      const path = '/admin/dashboard';
      console.log(`Debug - Explicitly saving path: ${path}`);
      sessionStorage.setItem('auth_redirect_path', path);
    }
  }, [isAuthenticated]);
  
  // Use the dashboard data hook
  const { 
    courses, 
    isLoading, 
    stats
  } = useDashboardData();

  if (!isAuthenticated || (isAuthenticated && user?.role !== 'admin')) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Header 
          title="Admin Dashboard" 
          description="Manage your courses and content"
        >
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/admin/files" className="flex items-center">
                <Files className="mr-2 h-4 w-4" />
                Files
              </Link>
            </Button>
            <Button asChild>
              <Link to="/admin/course/new" className="flex items-center">
                <PlusCircle className="mr-2 h-4 w-4" />
                New Course
              </Link>
            </Button>
          </div>
        </Header>
        
        {/* Stats Section */}
        <DashboardStats stats={stats} />
        
        {/* Courses Section */}
        <CoursesList 
          courses={courses} 
          isLoading={isLoading} 
          formatDate={formatDate} 
        />
      </main>
    </div>
  );
};

export default AdminDashboard;
